import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, authApi } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import {
  LogOut,
  Image as ImageIcon,
  Trash2,
  Pencil,
  Plus,
  X,
  Save,
  Sparkles,
  Settings as SettingsIcon,
  Upload,
} from "lucide-react";

const IMAGE_FIELDS = new Set(["image", "image_mobile", "logo", "thumb", "banner_image", "xray_image", "logo_image", "logo_image_footer", "siddha_image", "shravan_offer_image_1", "shravan_offer_image_2", "anniversary_image_1", "anniversary_image_2", "exhibition_hero_image", "exhibition_mala_image"]);
const PDF_FIELDS = new Set(["siddha_brochure_url", "brochure_url"]);

const FileUploadButton = ({ label = "Upload", accept = "image/*", onUploaded }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = () => inputRef.current && inputRef.current.click();

  const onChange = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setBusy(true);
    try {
      const res = await adminApi.uploadFile(f);
      onUploaded(res.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={onChange}
        className="hidden"
        data-testid="file-upload-input"
      />
      <button
        type="button"
        onClick={onPick}
        disabled={busy}
        data-cursor="button"
        data-testid="file-upload-btn"
        className="inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full bg-[#D4AF37] text-[#1A1412] hover:bg-[#B5952F] transition-colors disabled:opacity-60"
      >
        <Upload size={12} /> {busy ? "Uploading..." : label}
      </button>
    </>
  );
};

const TABS = [
  { key: "hero_banners", label: "Hero Banners", fields: [
    { k: "image", l: "Desktop Banner Image (1920 x 814 px)", type: "text" },
    { k: "image_mobile", l: "Mobile Banner Image (1080 x 1920 px portrait) — optional", type: "text" },
    { k: "title", l: "Title", type: "text" },
    { k: "subtitle", l: "Subtitle", type: "text" },
    { k: "link", l: "Click Link (optional — leave blank to disable click)", type: "text" },
    { k: "order", l: "Order", type: "number" },
    { k: "active", l: "Active", type: "bool" },
  ]},
  { key: "siddha_mala", label: "Siddha Mala Products (Legacy — not shown on home)", hidden: true, fields: [
    { k: "name", l: "Name", type: "text" },
    { k: "image", l: "Image URL", type: "text" },
    { k: "tagline", l: "Tagline (short one-liner)", type: "text" },
    { k: "description", l: "Description", type: "textarea" },
    { k: "price", l: "Price (₹)", type: "number" },
    { k: "discount_price", l: "Discount Price (₹)", type: "number" },
    { k: "link", l: "Buy Link (optional)", type: "text" },
    { k: "order", l: "Order", type: "number" },
    { k: "active", l: "Active", type: "bool" },
  ]},
  { key: "sankalp_pujas", label: "Sankalp Pujas", fields: [
    { k: "name", l: "Puja Name", type: "text" },
    { k: "month", l: "Month", type: "text" },
    { k: "image", l: "Card Image URL", type: "text" },
    { k: "banner_image", l: "Detail Banner URL", type: "text" },
    { k: "short_desc", l: "Short Description", type: "textarea" },
    { k: "long_desc", l: "About the Puja (multi-line — press ENTER for line break, empty line for spacing)", type: "textarea" },
    { k: "timing", l: "Timing (e.g. 8:00 AM - 12:00 PM)", type: "text" },
    { k: "pandit_count", l: "Number of Pandits", type: "number" },
    { k: "price", l: "Dakshina (₹)", type: "number" },
    { k: "purpose", l: "Purpose (one-liner shown on card)", type: "text" },
    { k: "about_format", l: "About Format", type: "select", options: [{v: "paragraph", l: "Paragraph"}, {v: "bullets", l: "Bullet Points (each line = one bullet)"}] },
    { k: "prashad", l: "Prashad (multi-line — press ENTER for line break)", type: "textarea" },
    { k: "prashad_format", l: "Prashad Format", type: "select", options: [{v: "paragraph", l: "Paragraph"}, {v: "bullets", l: "Bullet Points (each line = one bullet)"}] },
    { k: "benefits", l: "Benefits (comma-separated)", type: "list" },
    { k: "ingredients", l: "Ingredients used (comma-separated)", type: "list" },
    { k: "gallery", l: "Gallery URLs (comma-separated)", type: "list" },
    { k: "inquiry_link", l: "Participate/Inquiry Link (Rudralife URL)", type: "text" },
    { k: "order", l: "Order", type: "number" },
    { k: "active", l: "Active", type: "bool" },
  ]},
  { key: "rare_collection", label: "Rare Collection (21-29 Mukhi)", fields: [
    { k: "name", l: "Name (e.g. 21 Mukhi Rudraksha)", type: "text" },
    { k: "image", l: "Photo URL", type: "text" },
    { k: "xray_image", l: "X-Ray Image URL (optional)", type: "text" },
    { k: "tagline", l: "Tagline", type: "text" },
    { k: "size_weight", l: "Size & Weight", type: "text" },
    { k: "bullets", l: "Bullet Points (comma-separated)", type: "list" },
    { k: "price", l: "Price (₹) - optional", type: "number" },
    { k: "link", l: "Click Link — jab user is image pe click kare, is URL pe jaayega", type: "text" },
    { k: "order", l: "Order", type: "number" },
    { k: "active", l: "Active", type: "bool" },
  ]},
  { key: "exhibitions", label: "Exhibitions", fields: [
    { k: "city", l: "City", type: "text" },
    { k: "country", l: "Country", type: "text" },
    { k: "venue", l: "Venue", type: "text" },
    { k: "date_range", l: "Date Range (e.g. 1 - 5 Feb 2026)", type: "text" },
    { k: "status", l: "Status (live or upcoming)", type: "text" },
    { k: "image", l: "Image URL (optional)", type: "text" },
    { k: "link", l: "Register Link (optional)", type: "text" },
    { k: "order", l: "Order", type: "number" },
    { k: "active", l: "Active", type: "bool" },
  ]},
  { key: "combo_kits", label: "Rudraksha Combo Kits", fields: [
    { k: "name", l: "Name", type: "text" },
    { k: "image", l: "Image URL", type: "text" },
    { k: "price", l: "Original Price (₹) — strike-through", type: "number" },
    { k: "discount_price", l: "Sale Price (₹)", type: "number" },
    { k: "benefits", l: "One-liner Benefit", type: "text" },
    { k: "bullets", l: "What's Inside — Red Dot Bullets (comma-separated)", type: "list" },
    { k: "description", l: "Long Description (optional)", type: "textarea" },
    { k: "buy_link", l: "Buy Link (optional)", type: "text" },
    { k: "order", l: "Order", type: "number" },
    { k: "active", l: "Active", type: "bool" },
  ]},
  { key: "experts", label: "Panel Experts", fields: [
    { k: "name", l: "Name", type: "text" },
    { k: "role", l: "Role", type: "text" },
    { k: "image", l: "Image URL", type: "text" },
    { k: "bio", l: "Bio", type: "textarea" },
    { k: "order", l: "Order", type: "number" },
    { k: "active", l: "Active", type: "bool" },
  ]},
  { key: "testimonials", label: "Trusted Clients / Seekers", fields: [
    { k: "name", l: "Name", type: "text" },
    { k: "role", l: "Role", type: "text" },
    { k: "image", l: "Photo / Logo (Recommended 800 × 800 px · square)", type: "text" },
    { k: "link", l: "Optional Link (click on card goes here)", type: "text" },
    { k: "order", l: "Order", type: "number" },
    { k: "active", l: "Active", type: "bool" },
  ]},
  { key: "video_testimonials", label: "Video Testimonials", fields: [
    { k: "name", l: "Title", type: "text" },
    { k: "thumb", l: "Thumbnail URL (portrait 9:16)", type: "text" },
    { k: "video_link", l: "Video Link (YouTube/Reels)", type: "text" },
    { k: "quote", l: "Short Quote", type: "textarea" },
    { k: "order", l: "Order", type: "number" },
    { k: "active", l: "Active", type: "bool" },
  ]},
  { key: "media_features", label: "Featured Media Logos", fields: [
    { k: "name", l: "Media Name", type: "text" },
    { k: "logo", l: "Logo URL (transparent PNG/SVG)", type: "text" },
    { k: "link", l: "Article Link", type: "text" },
    { k: "order", l: "Order", type: "number" },
    { k: "active", l: "Active", type: "bool" },
  ]},
  { key: "blogs", label: "Blogs (Home Page)", fields: [
    { k: "title", l: "Title", type: "text" },
    { k: "image", l: "Cover Image URL", type: "text" },
    { k: "excerpt", l: "Excerpt (2-3 lines)", type: "textarea" },
    { k: "category", l: "Category / Tag", type: "text" },
    { k: "date", l: "Publish Date (e.g. Feb 10, 2026)", type: "text" },
    { k: "link", l: "External Blog Link (rudralife.com/blog/...)", type: "text" },
    { k: "order", l: "Order", type: "number" },
    { k: "active", l: "Active", type: "bool" },
  ]},
];

const emptyForm = (fields) =>
  fields.reduce((acc, f) => {
    if (f.type === "bool") acc[f.k] = true;
    else if (f.type === "number") acc[f.k] = 0;
    else if (f.type === "list") acc[f.k] = "";
    else acc[f.k] = "";
    return acc;
  }, {});

const AdminDashboard = () => {
  const nav = useNavigate();
  const [checked, setChecked] = useState(false);
  const [tabKey, setTabKey] = useState("__site__");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // form obj or null
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(null);

  const tab = useMemo(() => TABS.find((t) => t.key === tabKey), [tabKey]);

  useEffect(() => {
    const token = localStorage.getItem("rl_token");
    if (!token) {
      nav("/admin/login");
      return;
    }
    authApi
      .me()
      .then(() => setChecked(true))
      .catch(() => {
        localStorage.removeItem("rl_token");
        nav("/admin/login");
      });
  }, [nav]);

  useEffect(() => {
    if (!checked) return;
    if (tabKey === "__site__") {
      openSiteEditor();
      return;
    }
    if (tab) loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, tabKey]);

  const loadItems = async () => {
    try {
      const data = await adminApi.list(tabKey);
      setItems(data);
    } catch (e) {
      toast.error("Failed to load items");
    }
  };

  const openNew = () => setEditing({ ...emptyForm(tab.fields) });
  const openEdit = (it) => {
    const clone = { ...it };
    tab.fields.forEach((f) => {
      if (f.type === "list" && Array.isArray(clone[f.k])) clone[f.k] = clone[f.k].join(", ");
    });
    setEditing(clone);
  };

  const save = async () => {
    const payload = { ...editing };
    tab.fields.forEach((f) => {
      if (f.type === "list") {
        payload[f.k] = String(payload[f.k] || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      if (f.type === "number") {
        payload[f.k] = Number(payload[f.k] || 0);
      }
    });
    try {
      if (editing.id) {
        await adminApi.update(tabKey, editing.id, payload);
        toast.success("Updated");
      } else {
        await adminApi.create(tabKey, payload);
        toast.success("Created");
      }
      setEditing(null);
      loadItems();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await adminApi.remove(tabKey, id);
      toast.success("Deleted");
      loadItems();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const openSettings = async () => {
    try {
      const s = await adminApi.getSettings();
      setSettings(s);
      setShowSettings(true);
    } catch (e) {
      toast.error("Failed to load settings");
    }
  };

  const openSiteEditor = async () => {
    try {
      const s = await adminApi.getSettings();
      setSettings(s);
    } catch (e) {
      toast.error("Failed to load site content");
    }
  };

  const saveSettings = async () => {
    try {
      await adminApi.updateSettings(settings);
      toast.success("Settings saved");
      setShowSettings(false);
    } catch (e) {
      toast.error("Settings save failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("rl_token");
    localStorage.removeItem("rl_email");
    nav("/admin/login");
  };

  if (!checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF6F0]">
        <p className="font-serif text-xl text-[#5C544F]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF6F0] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#1A1412] text-white flex flex-col p-6 sticky top-0 h-screen">
        <div className="flex items-center gap-3 mb-10">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center font-serif text-xl"
            style={{ background: "radial-gradient(circle at 30% 30%, #b46a2a, #6b2f0a 70%, #2a1005)" }}
          >
            ॐ
          </div>
          <div>
            <div className="font-serif text-xl">Rudralife</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">Admin</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto">
          <button
            data-cursor="button"
            data-testid="admin-tab-site-content"
            onClick={() => { setTabKey("__site__"); setShowSettings(false); openSiteEditor(); }}
            className={`w-full text-left px-4 py-2.5 rounded-lg text-sm transition-colors font-semibold flex items-center gap-2 ${
              tabKey === "__site__"
                ? "bg-[#D4AF37] text-[#1A1412]"
                : "text-[#D4AF37] hover:text-white hover:bg-white/5"
            }`}
          >
            <SettingsIcon size={14} /> Site Content (One Page)
          </button>
          <div className="text-[9px] tracking-[0.3em] uppercase text-white/40 mt-4 mb-1 px-2">Collections</div>
          {TABS.filter((t) => !t.hidden).map((t) => (
            <button
              key={t.key}
              data-testid={ADMIN.tab(t.key)}
              data-cursor="button"
              onClick={() => setTabKey(t.key)}
              className={`w-full text-left px-4 py-2.5 rounded-lg text-sm transition-colors ${
                tabKey === t.key
                  ? "bg-[#8B2323] text-white"
                  : "text-white/70 hover:text-white hover:bg-white/5"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <button
          onClick={logout}
          data-testid={ADMIN.logoutBtn}
          data-cursor="button"
          className="flex items-center gap-2 text-sm text-white/70 hover:text-[#D4AF37] mt-4"
        >
          <LogOut size={14} /> Logout
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 p-8 md:p-12 overflow-y-auto">
        {tabKey === "__site__" ? (
          <SiteContentEditor
            settings={settings}
            setSettings={setSettings}
            onSave={saveSettings}
          />
        ) : (
          <>
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="text-xs tracking-[0.35em] uppercase text-[#8B2323] mb-2">Manage</div>
                <h1 className="font-serif text-4xl text-[#2D2825]">{tab.label}</h1>
              </div>
              <button
                onClick={openNew}
                data-testid={ADMIN.addBtn}
                data-cursor="button"
                className="inline-flex items-center gap-2 bg-[#8B2323] hover:bg-[#6B1A1A] text-white px-5 py-2.5 rounded-full text-sm tracking-widest uppercase"
              >
                <Plus size={16} /> Add New
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {items.map((it) => (
                <div key={it.id} className="bg-white rounded-2xl border border-[rgba(45,40,37,0.08)] overflow-hidden">
                  {(it.image || it.logo || it.thumb) && (
                    <div className="aspect-[16/9] overflow-hidden bg-[#F3EBE1]">
                      <img src={it.image || it.logo || it.thumb} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-serif text-lg text-[#2D2825] truncate">
                          {it.name || it.title || "Untitled"}
                        </h3>
                        <p className="text-xs text-[#5C544F] mt-1 line-clamp-2">
                          {it.description || it.short_desc || it.subtitle || it.link || ""}
                        </p>
                      </div>
                      <span
                        className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded-full ${
                          it.active
                            ? "bg-[#2E7D32]/10 text-[#2E7D32]"
                        : "bg-[#5C544F]/10 text-[#5C544F]"
                    }`}
                  >
                    {it.active ? "Live" : "Off"}
                  </span>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    data-testid={ADMIN.editBtn(it.id)}
                    data-cursor="button"
                    onClick={() => openEdit(it)}
                    className="flex-1 inline-flex items-center justify-center gap-1 border border-[#8B2323]/30 text-[#8B2323] py-2 rounded-lg text-xs uppercase tracking-widest hover:bg-[#8B2323] hover:text-white transition-colors"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                  <button
                    data-testid={ADMIN.deleteBtn(it.id)}
                    data-cursor="button"
                    onClick={() => remove(it.id)}
                    className="inline-flex items-center justify-center gap-1 border border-[#D32F2F]/40 text-[#D32F2F] px-3 py-2 rounded-lg text-xs uppercase tracking-widest hover:bg-[#D32F2F] hover:text-white transition-colors"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="col-span-full py-24 text-center text-[#5C544F]">
              <Sparkles className="mx-auto mb-3 text-[#D4AF37]" />
              <p>No items yet. Click &ldquo;Add New&rdquo; to create your first.</p>
            </div>
          )}
        </div>
          </>
        )}
      </main>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-[rgba(45,40,37,0.08)] px-6 py-4 flex items-center justify-between">
              <h3 className="font-serif text-2xl text-[#2D2825]">
                {editing.id ? "Edit" : "Add"} {tab.label}
              </h3>
              <button data-cursor="button" onClick={() => setEditing(null)} className="text-[#5C544F] hover:text-[#8B2323]">
                <X />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {tab.fields.map((f) => (
                <div key={f.k}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs tracking-widest uppercase text-[#5C544F]">{f.l}</label>
                    {IMAGE_FIELDS.has(f.k) && (
                      <FileUploadButton
                        label="Upload Image"
                        accept="image/*"
                        onUploaded={(url) => setEditing({ ...editing, [f.k]: url })}
                      />
                    )}
                    {PDF_FIELDS.has(f.k) && (
                      <FileUploadButton
                        label="Upload PDF"
                        accept=".pdf,application/pdf"
                        onUploaded={(url) => setEditing({ ...editing, [f.k]: url })}
                      />
                    )}
                  </div>
                  {f.type === "textarea" ? (
                    <textarea
                      rows={3}
                      value={editing[f.k] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.k]: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] focus:border-[#8B2323] outline-none"
                    />
                  ) : f.type === "bool" ? (
                    <label data-cursor="button" className="inline-flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!editing[f.k]}
                        onChange={(e) => setEditing({ ...editing, [f.k]: e.target.checked })}
                        className="w-5 h-5 accent-[#8B2323]"
                      />
                      <span className="text-sm">Show on site</span>
                    </label>
                  ) : f.type === "select" ? (
                    <select
                      value={editing[f.k] ?? f.options?.[0]?.v ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.k]: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] focus:border-[#8B2323] outline-none"
                    >
                      {(f.options || []).map((opt) => (
                        <option key={opt.v} value={opt.v}>{opt.l}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={f.type === "number" ? "number" : "text"}
                      value={editing[f.k] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.k]: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] focus:border-[#8B2323] outline-none"
                    />
                  )}
                  {IMAGE_FIELDS.has(f.k) && editing[f.k] && (
                    <img src={editing[f.k]} alt="" className="mt-2 h-24 rounded-lg object-cover border border-[rgba(45,40,37,0.08)]" />
                  )}
                </div>
              ))}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={save}
                  data-testid={ADMIN.saveBtn}
                  data-cursor="button"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#8B2323] hover:bg-[#6B1A1A] text-white py-3 rounded-full text-sm uppercase tracking-widest"
                >
                  <Save size={14} /> Save
                </button>
                <button
                  onClick={() => setEditing(null)}
                  data-cursor="button"
                  className="px-6 rounded-full border border-[rgba(45,40,37,0.2)] text-[#5C544F] hover:bg-[#F3EBE1]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && settings && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-[rgba(45,40,37,0.08)] px-6 py-4 flex items-center justify-between">
              <h3 className="font-serif text-2xl text-[#2D2825]">Site Settings</h3>
              <button data-cursor="button" onClick={() => setShowSettings(false)} className="text-[#5C544F] hover:text-[#8B2323]">
                <X />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SFld label="Brand Name" v={settings.brand_name} on={(v) => setSettings({ ...settings, brand_name: v })} />
                <SFld label="Tagline" v={settings.tagline} on={(v) => setSettings({ ...settings, tagline: v })} />
                <SFld label="WhatsApp Number (with country code)" v={settings.whatsapp_number} on={(v) => setSettings({ ...settings, whatsapp_number: v })} />
                <SFld label="Phone" v={settings.phone} on={(v) => setSettings({ ...settings, phone: v })} />
                <SFld label="Email" v={settings.email} on={(v) => setSettings({ ...settings, email: v })} />
                <SFld label="Open Days" v={settings.open_days} on={(v) => setSettings({ ...settings, open_days: v })} />
                <SFld label="Open Time" v={settings.open_time} on={(v) => setSettings({ ...settings, open_time: v })} />
                <SFld label="Address" v={settings.address} on={(v) => setSettings({ ...settings, address: v })} />
              </div>

              <div className="pt-4 border-t border-[rgba(45,40,37,0.08)]">
                <h4 className="font-serif text-lg text-[#2D2825] mb-3">Social Media</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {["facebook", "instagram", "youtube", "twitter"].map((k) => (
                    <SFld
                      key={k}
                      label={k.charAt(0).toUpperCase() + k.slice(1)}
                      v={settings.social?.[k] || ""}
                      on={(v) => setSettings({ ...settings, social: { ...(settings.social || {}), [k]: v } })}
                    />
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-[rgba(45,40,37,0.08)]">
                <h4 className="font-serif text-lg text-[#2D2825] mb-3">Brand & Logo</h4>
                <div className="grid grid-cols-1 gap-4">
                  <SFldImage label="Header Logo — Brown (upload from folder)" v={settings.logo_image} on={(v) => setSettings({ ...settings, logo_image: v })} />
                  <SFldImage label="Footer Logo — White (upload from folder)" v={settings.logo_image_footer} on={(v) => setSettings({ ...settings, logo_image_footer: v })} />
                </div>
              </div>

              <div className="pt-4 border-t border-[rgba(45,40,37,0.08)]">
                <h4 className="font-serif text-lg text-[#2D2825] mb-3">Combo Kits — Final Shop Now Button</h4>
                <div className="grid grid-cols-1 gap-4">
                  <SFld label="Combo Kits Shop Now Link (where the single red button navigates)" v={settings.combo_shop_link} on={(v) => setSettings({ ...settings, combo_shop_link: v })} />
                </div>
              </div>

              <div className="pt-4 border-t border-[rgba(45,40,37,0.08)]">
                <h4 className="font-serif text-lg text-[#2D2825] mb-3">Siddha Mala Feature</h4>
                <div className="grid grid-cols-1 gap-4">
                  <SFldImage label="Siddha Image (upload from folder)" v={settings.siddha_image} on={(v) => setSettings({ ...settings, siddha_image: v })} />
                  <SFld label="Heading (Red)" v={settings.siddha_heading} on={(v) => setSettings({ ...settings, siddha_heading: v })} />
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Intro Paragraph</label>
                    <textarea rows={3} value={settings.siddha_intro || ""} onChange={(e) => setSettings({ ...settings, siddha_intro: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
                  </div>
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Bullet Points (one per line)</label>
                    <textarea rows={6} value={(settings.siddha_bullets || []).join("\n")} onChange={(e) => setSettings({ ...settings, siddha_bullets: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
                  </div>
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Footer Line</label>
                    <textarea rows={2} value={settings.siddha_footer || ""} onChange={(e) => setSettings({ ...settings, siddha_footer: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
                  </div>
                  <SFldPdf label="Brochure PDF (upload PDF file)" v={settings.siddha_brochure_url} on={(v) => setSettings({ ...settings, siddha_brochure_url: v })} />
                  <SFld label="Brochure CTA Text" v={settings.siddha_brochure_cta} on={(v) => setSettings({ ...settings, siddha_brochure_cta: v })} />
                </div>
              </div>

              <div className="pt-4 border-t border-[rgba(45,40,37,0.08)]">
                <h4 className="font-serif text-lg text-[#2D2825] mb-3">Shravan Pujas Section</h4>
                <div className="grid grid-cols-1 gap-4">
                  <SFld label="Section Title" v={settings.shravan_pujas_title} on={(v) => setSettings({ ...settings, shravan_pujas_title: v })} />
                  <SFld label="Section Subtitle" v={settings.shravan_pujas_subtitle} on={(v) => setSettings({ ...settings, shravan_pujas_subtitle: v })} />
                </div>
              </div>

              <div className="pt-4 border-t border-[rgba(45,40,37,0.08)]">
                <h4 className="font-serif text-lg text-[#2D2825] mb-3">Exhibition Hero Section</h4>
                <div className="grid grid-cols-1 gap-4">
                  <SFld label="Title" v={settings.exhibition_hero_title} on={(v) => setSettings({ ...settings, exhibition_hero_title: v })} />
                  <SFld label="Subtitle" v={settings.exhibition_hero_subtitle} on={(v) => setSettings({ ...settings, exhibition_hero_subtitle: v })} />
                  <SFldImage label="Background Image (upload from folder)" v={settings.exhibition_hero_image} on={(v) => setSettings({ ...settings, exhibition_hero_image: v })} />
                  <SFld label="Link (where click goes)" v={settings.exhibition_hero_link} on={(v) => setSettings({ ...settings, exhibition_hero_link: v })} />
                </div>
              </div>

              <div className="pt-4 border-t border-[rgba(45,40,37,0.08)]">
                <h4 className="font-serif text-lg text-[#2D2825] mb-3">Shravan Offer Blog</h4>
                <div className="grid grid-cols-1 gap-4">
                  <SFld label="Section Title" v={settings.shravan_offer_title} on={(v) => setSettings({ ...settings, shravan_offer_title: v })} />
                  <SFldImage label="Image 1 (upload from folder)" v={settings.shravan_offer_image_1} on={(v) => setSettings({ ...settings, shravan_offer_image_1: v })} />
                  <SFldImage label="Image 2 (upload from folder)" v={settings.shravan_offer_image_2} on={(v) => setSettings({ ...settings, shravan_offer_image_2: v })} />
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Paragraph 1</label>
                    <textarea rows={3} value={settings.shravan_offer_paragraph_1 || ""} onChange={(e) => setSettings({ ...settings, shravan_offer_paragraph_1: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
                  </div>
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Paragraph 2</label>
                    <textarea rows={3} value={settings.shravan_offer_paragraph_2 || ""} onChange={(e) => setSettings({ ...settings, shravan_offer_paragraph_2: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-[rgba(45,40,37,0.08)]">
                <h4 className="font-serif text-lg text-[#2D2825] mb-3">25 Years Anniversary</h4>
                <div className="grid grid-cols-1 gap-4">
                  <SFld label="Title" v={settings.anniversary_title} on={(v) => setSettings({ ...settings, anniversary_title: v })} />
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Body Text</label>
                    <textarea rows={4} value={settings.anniversary_body || ""} onChange={(e) => setSettings({ ...settings, anniversary_body: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
                  </div>
                  <SFldImage label="Image 1 (upload from folder)" v={settings.anniversary_image_1} on={(v) => setSettings({ ...settings, anniversary_image_1: v })} />
                  <SFldImage label="Image 2 (upload from folder)" v={settings.anniversary_image_2} on={(v) => setSettings({ ...settings, anniversary_image_2: v })} />
                </div>
              </div>

              <div className="pt-4 border-t border-[rgba(45,40,37,0.08)]">
                <h4 className="font-serif text-lg text-[#2D2825] mb-3">Auto-Scroll Speeds (seconds; 0 = paused)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SFld num label="Hero Banner (sec)" v={settings.hero_speed_seconds} on={(v) => setSettings({ ...settings, hero_speed_seconds: Number(v) })} />
                  <SFld num label="Rare Collection (sec)" v={settings.rare_speed_seconds} on={(v) => setSettings({ ...settings, rare_speed_seconds: Number(v) })} />
                  <SFld num label="Exhibition (sec)" v={settings.exhibition_speed_seconds} on={(v) => setSettings({ ...settings, exhibition_speed_seconds: Number(v) })} />
                  <SFld num label="Combo Kits (sec)" v={settings.combo_speed_seconds} on={(v) => setSettings({ ...settings, combo_speed_seconds: Number(v) })} />
                  <SFld num label="Experts (sec)" v={settings.experts_speed_seconds} on={(v) => setSettings({ ...settings, experts_speed_seconds: Number(v) })} />
                </div>
              </div>

              <div className="pt-4 border-t border-[rgba(45,40,37,0.08)]">
                <h4 className="font-serif text-lg text-[#2D2825] mb-3">Stats & About</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SFld label="Seekers Count" v={settings.stat_seekers} on={(v) => setSettings({ ...settings, stat_seekers: v })} />
                  <SFld label="Years of Expertise" v={settings.stat_years} on={(v) => setSettings({ ...settings, stat_years: v })} />
                  <SFld label="Total Exhibitions" v={settings.stat_exhibitions} on={(v) => setSettings({ ...settings, stat_exhibitions: v })} />
                  <SFld label="Countries" v={settings.stat_countries} on={(v) => setSettings({ ...settings, stat_countries: v })} />
                </div>
                <div className="mt-4">
                  <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">About Text</label>
                  <textarea
                    rows={4}
                    value={settings.about_text || ""}
                    onChange={(e) => setSettings({ ...settings, about_text: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] focus:border-[#8B2323] outline-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={saveSettings}
                  data-cursor="button"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#8B2323] hover:bg-[#6B1A1A] text-white py-3 rounded-full text-sm uppercase tracking-widest"
                >
                  <Save size={14} /> Save Settings
                </button>
                <button
                  onClick={() => setShowSettings(false)}
                  data-cursor="button"
                  className="px-6 rounded-full border border-[rgba(45,40,37,0.2)] text-[#5C544F] hover:bg-[#F3EBE1]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SFld = ({ label, v, on, num }) => (
  <div>
    <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">{label}</label>
    <input
      type={num ? "number" : "text"}
      step="any"
      value={v ?? ""}
      onChange={(e) => on(e.target.value)}
      className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] focus:border-[#8B2323] outline-none"
    />
  </div>
);

const SFldImage = ({ label, v, on }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="block text-xs tracking-widest uppercase text-[#5C544F]">{label}</label>
      <FileUploadButton label="Upload Image" accept="image/*" onUploaded={on} />
    </div>
    <input
      type="text"
      value={v ?? ""}
      onChange={(e) => on(e.target.value)}
      className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] focus:border-[#8B2323] outline-none"
      placeholder="Paste URL or click Upload"
    />
    {v && <img src={v} alt="" className="mt-2 h-20 rounded-lg object-contain bg-[#F5EEDD] border border-[rgba(45,40,37,0.08)] p-1" />}
  </div>
);

const SFldPdf = ({ label, v, on }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="block text-xs tracking-widest uppercase text-[#5C544F]">{label}</label>
      <FileUploadButton label="Upload PDF" accept=".pdf,application/pdf" onUploaded={on} />
    </div>
    <input
      type="text"
      value={v ?? ""}
      onChange={(e) => on(e.target.value)}
      className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] focus:border-[#8B2323] outline-none"
      placeholder="Paste URL or click Upload"
    />
    {v && (
      <a href={v} target="_blank" rel="noreferrer" className="mt-2 inline-block text-[10px] tracking-widest uppercase text-[#8B2323] hover:underline">
        Open Current PDF ↗
      </a>
    )}
  </div>
);

/**
 * SiteContentEditor — a single unified page that lets admin edit ALL text/images/PDFs
 * used across the home page in ONE scrolling form. Each save posts to /api/admin/settings.
 */
const Section = ({ title, subtitle, children }) => (
  <section className="bg-white rounded-2xl border border-[rgba(45,40,37,0.08)] p-6 md:p-8 mb-6">
    <div className="mb-5 pb-4 border-b border-[rgba(45,40,37,0.06)]">
      <h3 className="font-serif text-xl md:text-2xl text-[#2D2825]">{title}</h3>
      {subtitle && <p className="text-xs text-[#5C544F] mt-1">{subtitle}</p>}
    </div>
    <div className="grid grid-cols-1 gap-4">{children}</div>
  </section>
);

const FaqEditor = ({ items = [], onChange }) => {
  const update = (i, patch) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    onChange(next);
  };
  const remove = (i) => {
    if (!window.confirm("Delete this FAQ?")) return;
    onChange(items.filter((_, idx) => idx !== i));
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () => {
    onChange([...items, { q: "New question?", a: "New answer goes here." }]);
  };

  return (
    <div className="space-y-4">
      {items.length === 0 && (
        <div className="text-center text-sm text-[#5C544F] py-6 bg-[#FAF6F0] rounded-xl">
          No FAQ items yet. Click <b>Add FAQ</b> below.
        </div>
      )}
      {items.map((f, i) => (
        <div
          key={i}
          data-testid={`admin-faq-item-${i}`}
          className="p-4 md:p-5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.1)] space-y-3"
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] tracking-[0.35em] uppercase text-[#8B2323]">
              FAQ #{i + 1}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                data-testid={`admin-faq-up-${i}`}
                data-cursor="button"
                className="text-[10px] px-2.5 py-1 rounded-full bg-white border border-[rgba(45,40,37,0.15)] text-[#5C544F] hover:bg-[#F3EBE1] disabled:opacity-40"
              >
                ↑ Up
              </button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                disabled={i === items.length - 1}
                data-testid={`admin-faq-down-${i}`}
                data-cursor="button"
                className="text-[10px] px-2.5 py-1 rounded-full bg-white border border-[rgba(45,40,37,0.15)] text-[#5C544F] hover:bg-[#F3EBE1] disabled:opacity-40"
              >
                ↓ Down
              </button>
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`admin-faq-delete-${i}`}
                data-cursor="button"
                className="text-[10px] px-2.5 py-1 rounded-full bg-[#8B2323] hover:bg-[#6B1A1A] text-white inline-flex items-center gap-1"
              >
                <Trash2 size={11} /> Delete
              </button>
            </div>
          </div>
          <div>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C544F] mb-1.5">
              Question
            </label>
            <input
              type="text"
              value={f.q || ""}
              onChange={(e) => update(i, { q: e.target.value })}
              data-testid={`admin-faq-q-${i}`}
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C544F] mb-1.5">
              Answer
            </label>
            <textarea
              rows={3}
              value={f.a || ""}
              onChange={(e) => update(i, { a: e.target.value })}
              data-testid={`admin-faq-a-${i}`}
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]"
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        data-testid="admin-faq-add"
        data-cursor="button"
        className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-[#D4AF37] hover:bg-[#B5952F] text-[#1A1412] text-xs tracking-[0.35em] uppercase font-medium"
      >
        <Plus size={14} /> Add FAQ
      </button>
    </div>
  );
};

const SiteContentEditor = ({ settings, setSettings, onSave }) => {
  const [previewOpen, setPreviewOpen] = useState(true);
  const [previewKey, setPreviewKey] = useState(0);

  if (!settings) {
    return (
      <div className="py-24 text-center text-[#5C544F]">
        <Sparkles className="mx-auto mb-3 text-[#D4AF37]" />
        <p>Loading site content...</p>
      </div>
    );
  }
  const set = (k, v) => setSettings({ ...settings, [k]: v });

  const handleSaveAndRefresh = async () => {
    await onSave();
    setPreviewKey((k) => k + 1);
  };

  return (
    <div className={previewOpen ? "grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,520px)] gap-6" : ""}>
      <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <div className="text-xs tracking-[0.35em] uppercase text-[#8B2323] mb-2">Full Page Editor</div>
          <h1 className="font-serif text-3xl md:text-4xl text-[#2D2825]">Site Content</h1>
          <p className="text-sm text-[#5C544F] mt-2 max-w-2xl">
            Har section ka text, image aur PDF yahin ek page pe edit karo. Save karne ke baad
            server refresh ho ya kuch bhi ho — aapka data safe rahega.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="toggle-preview-btn"
            data-cursor="button"
            onClick={() => setPreviewOpen((v) => !v)}
            className="hidden xl:inline-flex items-center gap-2 border border-[#D4AF37] text-[#8B2323] px-4 py-3 rounded-full text-xs tracking-widest uppercase hover:bg-[#D4AF37]/10"
          >
            {previewOpen ? "Hide Preview" : "Show Preview"}
          </button>
          <button
            type="button"
            onClick={handleSaveAndRefresh}
            data-testid="site-editor-save-top"
            data-cursor="button"
            className="inline-flex items-center gap-2 bg-[#8B2323] hover:bg-[#6B1A1A] text-white px-6 py-3 rounded-full text-sm tracking-widest uppercase transition-colors"
          >
            <Save size={14} /> Save & Refresh
          </button>
        </div>
      </div>

      <Section title="🎛️ Sections On/Off (हर section को show/hide करो)" subtitle="Toggle off करने पर data delete नहीं होगा — बस page पर hidden हो जाएगा">
        {[
          ["show_siddha_mala", "Siddha Mala"],
          ["show_shravan_pujas", "Sankalp / Shravan Pujas"],
          ["show_exhibition", "Exhibition Hero"],
          ["show_combo_kits", "Combo Kits"],
          ["show_shravan_offer", "Shravan Offer Blog"],
          ["show_rare_collection", "Rare Collection"],
          ["show_experts", "Panel Experts"],
          ["show_anniversary", "25 Years Anniversary"],
          ["show_trusted", "Trusted by Seekers"],
          ["show_blogs", "Blogs"],
          ["show_media", "Media Logos"],
          ["show_faq", "FAQ"],
        ].map(([k, label]) => (
          <label key={k} className="flex items-center justify-between p-3 rounded-xl bg-[#FAF6F0] cursor-pointer">
            <span className="text-sm font-medium text-[#2D2825]">{label}</span>
            <input
              type="checkbox"
              checked={settings[k] !== false}
              onChange={(e) => set(k, e.target.checked)}
              className="w-5 h-5 accent-[#8B2323]"
            />
          </label>
        ))}
      </Section>

      <Section title="📝 Formatting Tips" subtitle="Sabhi text/paragraph fields me ye HTML tags use kar sakte ho:">
        <div className="text-xs text-[#5C544F] leading-relaxed bg-[#FAF6F0] p-4 rounded-xl">
          <p><b>&lt;b&gt;bold text&lt;/b&gt;</b> — bold banane ke liye</p>
          <p><i>&lt;i&gt;italic text&lt;/i&gt;</i> — italic ke liye</p>
          <p><u>&lt;u&gt;underline&lt;/u&gt;</u> — underline ke liye</p>
          <p>&lt;span style=&quot;color:red&quot;&gt;<span style={{color:"red"}}>red text</span>&lt;/span&gt; — color: red, gold, #D4AF37, #8B2323, etc.</p>
          <p>Enter dabao line break ke liye. Empty line = blank space nichey.</p>
        </div>
      </Section>

      <Section title="Section Headings — Siddha Mala">
        <SFld label="Kicker (top small text)" v={settings.siddha_kicker} on={(v) => set("siddha_kicker", v)} />
      </Section>

      <Section title="Section Headings — Sankalp Pujas">
        <SFld label="Kicker" v={settings.shravan_pujas_kicker} on={(v) => set("shravan_pujas_kicker", v)} />
        <SFld label="Title" v={settings.shravan_pujas_title} on={(v) => set("shravan_pujas_title", v)} />
        <div>
          <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Subtitle</label>
          <textarea rows={3} value={settings.shravan_pujas_subtitle || ""} onChange={(e) => set("shravan_pujas_subtitle", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
        </div>
      </Section>

      <Section title="Section Headings — Combo Kits">
        <SFld label="Kicker" v={settings.combo_kicker} on={(v) => set("combo_kicker", v)} />
        <SFld label="Title Line 1" v={settings.combo_title_line1} on={(v) => set("combo_title_line1", v)} />
        <SFld label="Title Line 2" v={settings.combo_title_line2} on={(v) => set("combo_title_line2", v)} />
        <div>
          <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Footer Tagline (below cards, above Shop Now)</label>
          <textarea rows={2} value={settings.combo_footer_line || ""} onChange={(e) => set("combo_footer_line", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
        </div>
        <SFld label="Shop Now Button Text" v={settings.combo_shop_cta} on={(v) => set("combo_shop_cta", v)} />
      </Section>

      <Section title="Section Headings — Rare Collection">
        <SFld label="Kicker" v={settings.rare_kicker} on={(v) => set("rare_kicker", v)} />
        <SFld label="Title" v={settings.rare_title} on={(v) => set("rare_title", v)} />
        <div>
          <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Subtitle</label>
          <textarea rows={3} value={settings.rare_subtitle || ""} onChange={(e) => set("rare_subtitle", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
        </div>
      </Section>

      <Section title="Section Headings — Panel Experts">
        <SFld label="Kicker" v={settings.experts_kicker} on={(v) => set("experts_kicker", v)} />
        <SFld label="Title" v={settings.experts_title} on={(v) => set("experts_title", v)} />
        <div>
          <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Subtitle</label>
          <textarea rows={3} value={settings.experts_subtitle || ""} onChange={(e) => set("experts_subtitle", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
        </div>
      </Section>

      <Section title="Section Headings — Anniversary">
        <SFld label="Kicker" v={settings.anniversary_kicker} on={(v) => set("anniversary_kicker", v)} />
      </Section>

      <Section title="Section Headings — Trusted by Seekers">
        <SFld label="Kicker" v={settings.trusted_kicker} on={(v) => set("trusted_kicker", v)} />
        <SFld label="Title" v={settings.trusted_title} on={(v) => set("trusted_title", v)} />
        <div>
          <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Subtitle</label>
          <textarea rows={3} value={settings.trusted_subtitle || ""} onChange={(e) => set("trusted_subtitle", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
        </div>
      </Section>

      <Section title="Section Headings — Blogs">
        <SFld label="Kicker" v={settings.blogs_kicker} on={(v) => set("blogs_kicker", v)} />
        <SFld label="Title" v={settings.blogs_title} on={(v) => set("blogs_title", v)} />
      </Section>

      <Section title="Section Headings — FAQ">
        <SFld label="Kicker" v={settings.faq_kicker} on={(v) => set("faq_kicker", v)} />
        <SFld label="Title" v={settings.faq_title} on={(v) => set("faq_title", v)} />
      </Section>

      <Section title="❓ FAQ Items (A to Z Editing)" subtitle="Sabhi FAQs add/edit/delete/reorder yahin se karo. Har question ki accordion niche home page pe milegi.">
        <FaqEditor
          items={settings.faqs || []}
          onChange={(next) => set("faqs", next)}
        />
      </Section>

      <Section title="Section Headings — Shravan Offer Blog">
        <SFld label="Kicker" v={settings.shravan_offer_kicker} on={(v) => set("shravan_offer_kicker", v)} />
      </Section>

      <Section title="Brand & Logos" subtitle="Header aur footer ke logos.">
        <SFldImage label="Header Logo — Brown (upload from folder)" v={settings.logo_image} on={(v) => set("logo_image", v)} />
        <SFldImage label="Footer Logo — White (upload from folder)" v={settings.logo_image_footer} on={(v) => set("logo_image_footer", v)} />
        <SFld label="Brand Name" v={settings.brand_name} on={(v) => set("brand_name", v)} />
        <SFld label="Tagline" v={settings.tagline} on={(v) => set("tagline", v)} />
      </Section>

      <Section title="Siddha Mala Feature" subtitle="Ye Rudralife ka hero product hai. Sirf 1 image, 1 heading — sab kuch yahaan se control.">
        <SFldImage label="Siddha Mala Image" v={settings.siddha_image} on={(v) => set("siddha_image", v)} />
        <SFld label="Heading (Red)" v={settings.siddha_heading} on={(v) => set("siddha_heading", v)} />
        <div>
          <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Intro Paragraph</label>
          <textarea rows={3} value={settings.siddha_intro || ""} onChange={(e) => set("siddha_intro", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
        </div>
        <div>
          <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Bullet Points (ek line — ek bullet)</label>
          <textarea rows={6} value={(settings.siddha_bullets || []).join("\n")} onChange={(e) => set("siddha_bullets", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
        </div>
        <div>
          <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Footer Line</label>
          <textarea rows={2} value={settings.siddha_footer || ""} onChange={(e) => set("siddha_footer", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
        </div>
        <SFldPdf label="Brochure PDF (upload PDF file)" v={settings.siddha_brochure_url} on={(v) => set("siddha_brochure_url", v)} />
        <SFld label="Brochure CTA Text" v={settings.siddha_brochure_cta} on={(v) => set("siddha_brochure_cta", v)} />
      </Section>

      <Section title="Sankalp Pujas Section Header">
        <SFld label="Section Title" v={settings.shravan_pujas_title} on={(v) => set("shravan_pujas_title", v)} />
        <SFld label="Section Subtitle" v={settings.shravan_pujas_subtitle} on={(v) => set("shravan_pujas_subtitle", v)} />
      </Section>

      <Section title="Exhibition Hero" subtitle="Bada photo-banner section (Rudralife Exhibition).">
        <div className="pb-4 mb-2 border-b border-dashed border-[rgba(45,40,37,0.1)]">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#8B2323] mb-3">↑ Banner ke Upar (Top Header)</div>
          <div className="grid grid-cols-1 gap-4">
            <SFld label="Top Kicker (small red text — like 'Combinations Crafted With Purpose')" v={settings.exhibition_top_kicker} on={(v) => set("exhibition_top_kicker", v)} />
            <SFld label="Top Title Line 1 (gold italic — like 'The Rudraksha')" v={settings.exhibition_top_title_line1} on={(v) => set("exhibition_top_title_line1", v)} />
            <SFld label="Top Title Line 2 (gold italic — like 'Combo Kits')" v={settings.exhibition_top_title_line2} on={(v) => set("exhibition_top_title_line2", v)} />
          </div>
        </div>
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#8B2323] mb-1">↓ Banner ke Andar (Overlay)</div>
        <SFld label="Title" v={settings.exhibition_hero_title} on={(v) => set("exhibition_hero_title", v)} />
        <SFld label="Subtitle" v={settings.exhibition_hero_subtitle} on={(v) => set("exhibition_hero_subtitle", v)} />
        <SFldImage label="Background Image (clear, high-quality)" v={settings.exhibition_hero_image} on={(v) => set("exhibition_hero_image", v)} />
        <SFldImage label="3D Pop-Out Mala Image (transparent PNG, bottom bead pokes out)" v={settings.exhibition_mala_image} on={(v) => set("exhibition_mala_image", v)} />
        <SFld label="Link (click yaha jaayega)" v={settings.exhibition_hero_link} on={(v) => set("exhibition_hero_link", v)} />
        <div>
          <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Exhibition Paragraph — banner ke NEECHE dikhega</label>
          <textarea rows={5} value={settings.exhibition_content || ""} onChange={(e) => set("exhibition_content", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
        </div>
      </Section>

      <Section title="Combo Kits — Final Shop Now" subtitle="Sabhi combo kits ke neeche ek hi red 'Shop Now' button.">
        <SFld label="Combo Kits Shop Now Link" v={settings.combo_shop_link} on={(v) => set("combo_shop_link", v)} />
      </Section>

      <Section title="Shravan Offer Blog">
        <SFld label="Section Title" v={settings.shravan_offer_title} on={(v) => set("shravan_offer_title", v)} />
        <SFldImage label="Image" v={settings.shravan_offer_image_1} on={(v) => set("shravan_offer_image_1", v)} />
        <div>
          <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Paragraph Content</label>
          <textarea rows={5} value={settings.shravan_offer_paragraph_1 || ""} onChange={(e) => set("shravan_offer_paragraph_1", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
        </div>
      </Section>

      <Section title="25 Years Anniversary">
        <SFld label="Title" v={settings.anniversary_title} on={(v) => set("anniversary_title", v)} />
        <div>
          <label className="block text-xs tracking-widest uppercase text-[#5C544F] mb-2">Body Text</label>
          <textarea rows={4} value={settings.anniversary_body || ""} onChange={(e) => set("anniversary_body", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF6F0] border border-[rgba(45,40,37,0.15)] outline-none focus:border-[#8B2323]" />
        </div>
        <SFldImage label="Image 1" v={settings.anniversary_image_1} on={(v) => set("anniversary_image_1", v)} />
        <SFldImage label="Image 2" v={settings.anniversary_image_2} on={(v) => set("anniversary_image_2", v)} />
      </Section>

      <Section title="Contact & Social">
        <SFld label="WhatsApp Number (with country code)" v={settings.whatsapp_number} on={(v) => set("whatsapp_number", v)} />
        <SFld label="Phone" v={settings.phone} on={(v) => set("phone", v)} />
        <SFld label="Email" v={settings.email} on={(v) => set("email", v)} />
        <SFld label="Open Days" v={settings.open_days} on={(v) => set("open_days", v)} />
        <SFld label="Open Time" v={settings.open_time} on={(v) => set("open_time", v)} />
        <SFld label="Address" v={settings.address} on={(v) => set("address", v)} />
        {["facebook", "instagram", "youtube", "twitter"].map((k) => (
          <SFld
            key={k}
            label={k.charAt(0).toUpperCase() + k.slice(1) + " URL"}
            v={settings.social?.[k] || ""}
            on={(v) => setSettings({ ...settings, social: { ...(settings.social || {}), [k]: v } })}
          />
        ))}
      </Section>

      <Section title="Stats Numbers">
        <SFld label="Seekers Count" v={settings.stat_seekers} on={(v) => set("stat_seekers", v)} />
        <SFld label="Years of Expertise" v={settings.stat_years} on={(v) => set("stat_years", v)} />
        <SFld label="Total Exhibitions" v={settings.stat_exhibitions} on={(v) => set("stat_exhibitions", v)} />
        <SFld label="Countries" v={settings.stat_countries} on={(v) => set("stat_countries", v)} />
      </Section>

      <Section title="Auto-Scroll Speeds (seconds, 0 = paused)">
        <SFld num label="Hero Banner (sec)" v={settings.hero_speed_seconds} on={(v) => set("hero_speed_seconds", Number(v))} />
        <SFld num label="Rare Collection (sec)" v={settings.rare_speed_seconds} on={(v) => set("rare_speed_seconds", Number(v))} />
        <SFld num label="Exhibition (sec)" v={settings.exhibition_speed_seconds} on={(v) => set("exhibition_speed_seconds", Number(v))} />
        <SFld num label="Combo Kits (sec)" v={settings.combo_speed_seconds} on={(v) => set("combo_speed_seconds", Number(v))} />
        <SFld num label="Experts (sec)" v={settings.experts_speed_seconds} on={(v) => set("experts_speed_seconds", Number(v))} />
      </Section>

      <div className="sticky bottom-0 -mx-8 md:-mx-12 px-8 md:px-12 py-5 bg-gradient-to-t from-[#FAF6F0] via-[#FAF6F0] to-transparent">
        <button
          type="button"
          onClick={handleSaveAndRefresh}
          data-testid="site-editor-save-bottom"
          data-cursor="button"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#8B2323] hover:bg-[#6B1A1A] text-white py-4 rounded-full text-sm tracking-widest uppercase transition-colors shadow-[0_15px_35px_rgba(139,35,35,0.3)]"
        >
          <Save size={14} /> Save & Refresh Preview
        </button>
      </div>
      </div>

      {/* Live Preview Pane (visible on XL+ screens when open) */}
      {previewOpen && (
        <aside className="hidden xl:block sticky top-6 self-start" style={{ height: "calc(100vh - 3rem)" }}>
          <div className="h-full bg-white rounded-2xl border border-[rgba(45,40,37,0.08)] overflow-hidden flex flex-col shadow-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(45,40,37,0.06)] bg-[#F3EBE1]">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#28C940]" />
                </div>
                <span className="text-xs tracking-widest uppercase text-[#5C544F] font-medium ml-2">Live Preview</span>
              </div>
              <button
                type="button"
                onClick={() => setPreviewKey((k) => k + 1)}
                data-testid="preview-refresh-btn"
                data-cursor="button"
                className="text-[10px] tracking-widest uppercase text-[#8B2323] hover:text-[#6B1A1A]"
              >
                Refresh
              </button>
            </div>
            <iframe
              key={previewKey}
              src="/events"
              title="Homepage Preview"
              className="flex-1 w-full border-0 bg-[#FAF6F0]"
              data-testid="site-preview-iframe"
            />
          </div>
        </aside>
      )}
    </div>
  );
};

export default AdminDashboard;
